/**
 * WONE-568: minimal auth context for the admin portal.
 *
 * Wraps the OAuth substrate in ``src/auth/tokens.js`` and exposes
 * ``useAuth()``. Only the surface the admin actually uses — no
 * learner/teacher branching, no method chooser, no OTP.
 *
 * Identity + role:
 *   ``/api/v1/auth/me`` only returns ``{id, email}`` — it has no role
 *   data. The admin portal gates on the ADMIN role, exactly as the
 *   legacy login did, by querying the wone-admin GraphQL ``user(email)``
 *   resolver for ``userdetail.role``. We also write the resolved user to
 *   the encrypted ``WonEUser`` localStorage key so the existing
 *   components that read ``functions.getLoginUser()`` (Sidebar, Header)
 *   keep working unchanged.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { gql, useApolloClient } from '@apollo/client';
import * as CryptoJS from 'crypto-js';

import {
  VerifyError,
  beginLogin,
  bootstrapSession,
  completeSSO,
  fetchMe,
  getAccessToken,
  revokeSession,
  setAccessToken,
} from './tokens';

const AuthContext = createContext(null);

// Same shape the legacy admin login fetched, so the encrypted
// ``WonEUser`` payload stays drop-in compatible with the components
// that read it via ``functions.getLoginUser()``.
const GET_USER_DETAILS = gql`
  query User($email: String!) {
    user(email: $email) {
      userdetail {
        photo
        role {
          id
          name
        }
      }
      id
      firstName
      lastName
      username
      lastLogin
      email
    }
  }
`;

function userIsAdmin(user) {
  const roles = user && user.userdetail && user.userdetail.role;
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => r && r.name === 'ADMIN');
}

function persistLegacyUser(user) {
  // Matches the legacy Login.jsx write so functions.getLoginUser()
  // (AES key "WonEUser") can decrypt it. Kept until the admin
  // components migrate off the WonEUser localStorage read.
  try {
    const encrypted = CryptoJS.AES.encrypt(
      JSON.stringify(user),
      'WonEUser'
    ).toString();
    localStorage.setItem('WonEUser', encrypted);
  } catch (err) {
    // Non-fatal: a failed write just means Sidebar/Header fall back to
    // their defaults. The React user state below is the source of truth.
  }
}

export const AuthProvider = ({ children }) => {
  const apollo = useApolloClient();
  const [user, setUser] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  // Resolve the signed-in admin: confirm the token (email via /me),
  // fetch role + profile via GraphQL, gate on ADMIN, and mirror to the
  // legacy WonEUser store.
  //
  // Returns the user when admin, or null for a definitive "not signed
  // in / not an admin" (token discarded). THROWS VerifyError on a
  // transient failure (network / 5xx) so the caller can keep the token
  // and surface "try again" instead of falsely reporting "not an admin".
  const loadUser = useCallback(async () => {
    const me = await fetchMe(); // throws VerifyError on transient failure
    const email = me && me.email;
    if (!email) {
      setUser(null);
      return null;
    }

    let fetched;
    try {
      const { data } = await apollo.query({
        query: GET_USER_DETAILS,
        variables: { email },
        fetchPolicy: 'no-cache',
      });
      fetched = data && data.user;
    } catch (err) {
      // Transport / 5xx → couldn't verify; keep the token and let the
      // caller retry. A GraphQL-level error (e.g. "User has no
      // userdetail" / permission denied) is a definitive answer → fall
      // through and treat as non-admin.
      if (err && err.networkError) {
        // eslint-disable-next-line no-console
        console.error('[auth] role lookup failed (transient) — could not verify admin', err);
        throw new VerifyError();
      }
      fetched = null;
    }

    if (!fetched || !userIsAdmin(fetched)) {
      setAccessToken(null);
      setUser(null);
      return null;
    }

    persistLegacyUser(fetched);
    setUser(fetched);
    return fetched;
  }, [apollo]);

  // One-shot bootstrap on mount: refresh the access token (if a cookie
  // exists), then load the user. ``setBootstrapped(true)`` MUST run in
  // all paths (try/finally) — if it didn't, a thrown VerifyError would
  // leave the gate stuck rendering nothing forever.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await bootstrapSession();
        if (cancelled) return;
        if (token) {
          await loadUser();
        }
      } catch (err) {
        // Transient verify failure on cold boot → present the login
        // screen; the user can retry. Already logged in fetchMe/loadUser.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  // Redirect to Platform-hosted login (WONE-770). No provider hint →
  // Platform's full login page (password + SSO). The round-trip returns
  // via /auth/callback → completeSSOLogin below.
  const startLogin = useCallback((idpHint, returnTo) => {
    return beginLogin(idpHint, returnTo);
  }, []);

  const completeSSOLogin = useCallback(
    async (code, state) => {
      const returnTo = await completeSSO(code, state);
      const me = await loadUser();
      if (!me) throw new Error('not_admin');
      return { user: me, returnTo };
    },
    [loadUser]
  );

  const logout = useCallback(async () => {
    await revokeSession();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      bootstrapped,
      authed: !!user,
      startLogin,
      completeSSOLogin,
      logout,
      getAccessToken,
    }),
    [user, bootstrapped, startLogin, completeSSOLogin, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
};
